/**
 * Shared UI component for UI Datepicker.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';

import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

@Component({
  selector: 'ui-datepicker',
  standalone: true,
  imports: [FormsModule, DatePickerModule, UiLabelTranslatePipe],
  templateUrl: './ui-datepicker.component.html',
  styleUrl: './ui-datepicker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiDatepickerComponent {
  readonly label = input('');
  readonly value = input('');
  readonly placeholder = input('Select date');
  readonly id = input('');
  readonly disabled = input(false);
  readonly required = input(false);
  readonly error = input('');
  readonly hint = input('');
  readonly showIcon = input(true);
  readonly dateFormat = input('dd/mm/yy');
  readonly valueChange = output<string>();

  // computed so the Date reference is only recreated when value() actually changes,
  // preventing ngModel from seeing a new object on every change detection cycle.
  readonly dateValue = computed<Date | null>(() => {
    const raw = this.value();
    if (!raw) return null;
    // Strip time portion from ISO strings (e.g. "2026-04-29T00:00:00.000Z" → "2026-04-29")
    const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
    // Parse as local date parts to avoid UTC-midnight shifting the date by one day
    // in timezones behind UTC (e.g. GMT+1 reads 2024-01-15T00:00Z as Jan 14 locally).
    const [year, month, day] = datePart.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  });

  onDateChange(date: Date | null): void {
    if (!date) {
      this.valueChange.emit('');
      return;
    }
    // Serialize using local date parts so the emitted ISO string matches what the user picked.
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    this.valueChange.emit(`${y}-${m}-${d}`);
  }
}
